import type {
  PathSegmentContextRef,
  SearchResultItemData,
} from "../../../../infra/protected/Search/SearchResultData";
import { Parent } from "../../../../services/soap/project/requests/ListFPARequest";

const PARENT_TYPES: ReadonlySet<string> = new Set(["FOLDER", "PROJECT", "ACTIVITY"]);

export type ContextTypeKind = "FOLDER" | "PROJECT" | "ACTIVITY" | "";

export interface ContextObject {
  contextId: number;
  contextType: ContextTypeKind;
  link: string;
  /** Ordered segments for breadcrumb display (e.g. Folder > Project > Activity). */
  pathSegments: string[];
  /** Parallel to pathSegments for ListFPA parent scoping; omitted for legacy selections. */
  pathSegmentContexts?: PathSegmentContextRef[];
  /**
   * Folder type definition id (`ListFolderType` / GetContextPath `Context.type` on folder nodes).
   * Used for a static leading breadcrumb label only, not for search or navigation.
   */
  folderTypeId?: number;
}

export interface ContextSearchScope {
  parent: Parent | null;
  targetItemType?: PathSegmentContextRef["type"];
}

export interface ContextSelectionProps {
  value?: ContextObject | null;
  onSelectionChange: (context: ContextObject | null) => void;
  folderStatusMap: Record<string, unknown>;
  projectStatusMap: Record<string, unknown>;
  activityStatusMap: Record<string, unknown>;
  isReadOnly?: boolean;
  /** Max rows visible before scroll; default 5. */
  maxVisibleItems?: number;
  /** When false, hides borders on the collapsed label and the edit-mode search field. Default true. */
  showBorders?: boolean;
  /** Alias for {@link showBorders}; when set, overrides `showBorders`. */
  showBorder?: boolean;
  /** When true, the collapsed selected field uses a transparent background so it blends with the page. Default false. */
  allowTransparentBG?: boolean;
  /**
   * When false, uses a transparent collapsed background (same as `allowTransparentBG={true}`).
   * When true (default), uses the standard filled background unless `allowTransparentBG` is set.
   * If both are set, `allowTransparentBG` wins.
   */
  filledBackground?: boolean;
  /** When true, shows a clear control on the collapsed field when a context is selected. Default false. */
  allowClearSelection?: boolean;
  /** When true, shows an Add control in Results/Browse edit-mode list (not History). Default false. */
  allowAdd?: boolean;
  /** Invoked when the Add control is activated; button is disabled if omitted. */
  onAdd?: () => void;
  /**
   * One-shot initial context id (read only from the first render). If `value` is still empty
   * and `explicitContextId` was not set on mount, resolves via GetContextPath once and calls
   * `onSelectionChange`. Ignored on later renders if the prop reference changes.
   */
  defaultValue?: number | null;
  /**
   * When set, loads full hierarchy via GetContextPath and calls `onSelectionChange`
   * with the mapped {@link ContextObject}. Clear after resolve if you only want a one-shot load.
   */
  explicitContextId?: number | null;
  /** Invoked when GetContextPath fails or returns data that cannot be mapped. */
  onExplicitContextResolveError?: (error: unknown) => void;
  /** When true, the control mounts directly in search/edit mode. Default false. */
  startInEditMode?: boolean;
  className?: string;
  "aria-label"?: string;
}

/** Build display path segments from a search row (matches row semantics). */
export function pathSegmentsFromSearchItem(item: SearchResultItemData): string[] {
  return [...(item.breadcrumbs ?? []), item.title].filter(Boolean);
}

export function contextObjectFromSearchItem(item: SearchResultItemData): ContextObject {
  const folderTypeId =
    item.itemType === "FOLDER" &&
    item.contextTypeId != null &&
    item.contextTypeId > 0
      ? item.contextTypeId
      : undefined;
  return {
    contextId: item.itemId ?? 0,
    contextType: (item.itemType as ContextTypeKind) ?? "",
    link: item.link ?? "",
    pathSegments: pathSegmentsFromSearchItem(item),
    ...(item.pathSegmentContexts?.length
      ? { pathSegmentContexts: item.pathSegmentContexts }
      : {}),
    ...(folderTypeId != null ? { folderTypeId } : {}),
  };
}

/**
 * ListFPA parent for browsing **siblings** of the segment at `segmentIndex`.
 * Uses the parent node (`pathSegmentContexts[segmentIndex - 1]`). The first
 * segment has no parent in this array — returns `null` (caller may use global history).
 */
export function parentForSiblingBrowse(
  segmentIndex: number,
  pathSegmentContexts: PathSegmentContextRef[] | undefined
): Parent | null {
  if (!pathSegmentContexts?.length || segmentIndex <= 0) {
    return null;
  }
  const parentRef = pathSegmentContexts[segmentIndex - 1];
  if (!parentRef) {
    return null;
  }
  return new Parent(parentRef.id, parentRef.type);
}

/**
 * ListFPA parent for browsing **children** of the current selection (leaf node).
 */
export function parentForLeafChildren(context: ContextObject): Parent | null {
  const ctxs = context.pathSegmentContexts;
  if (ctxs?.length) {
    const leaf = ctxs[ctxs.length - 1];
    return new Parent(leaf.id, leaf.type);
  }
  const t = context.contextType;
  if (context.contextId && PARENT_TYPES.has(t)) {
    return new Parent(context.contextId, t);
  }
  return null;
}
